'use client';

import { Link as ChakraLink, Text } from '@chakra-ui/react';
import { Editor } from '@tiptap/react';
import * as React from 'react';
import VButton from '../VButton.jsx';
import VInput from '../VInput.jsx';
import VStackRow from '../VStackRow';
import { useRichTextEditorContext } from './VRichTextContext';

const ENTER_KEY = 'Enter';

const readHref = (editor: Editor): string => editor.getAttributes('link').href ?? '';

const isCaretInLink = (editor: Editor): boolean =>
  editor.isFocused && editor.state.selection.empty && editor.isActive('link');

const keepEditorFocused = (event: React.MouseEvent): void => event.preventDefault();

const applyHref = (editor: Editor, href: string): void => {
  const chain = editor.chain().focus().extendMarkRange('link');

  if (href === '') {
    chain.unsetLink().run();

    return;
  }

  chain.setLink({ href }).run();
};

interface LinkFormProps {
  editor: Editor;
  onClose: () => void;
}

const LinkForm = ({ editor, onClose }: LinkFormProps): React.ReactElement => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const save = (): void => {
    applyHref(editor, inputRef.current?.value.trim() ?? '');
    onClose();
  };

  const saveOnEnter = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== ENTER_KEY) {
      return;
    }

    event.preventDefault();
    save();
  };

  const closeWhenFocusLeaves = (event: React.FocusEvent<HTMLDivElement>): void => {
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }

    onClose();
  };

  return (
    <VStackRow gap={2} padding={2} borderTopWidth="1px" onBlur={closeWhenFocusLeaves}>
      <Text fontSize="xs" whiteSpace="nowrap">
        Enter link:
      </Text>
      <VInput
        ref={inputRef}
        aria-label="Link URL"
        defaultValue={readHref(editor)}
        placeholder="https://"
        onKeyDown={saveOnEnter}
      />
      <VButton onMouseDown={keepEditorFocused} onClick={save}>
        Save
      </VButton>
    </VStackRow>
  );
};

interface LinkPreviewProps {
  editor: Editor;
  onEdit: () => void;
}

const LinkPreview = ({ editor, onEdit }: LinkPreviewProps): React.ReactElement => {
  const href = readHref(editor);
  const remove = (): void => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
  };

  return (
    <VStackRow
      gap={2}
      padding={2}
      borderTopWidth="1px"
      fontSize="xs"
      onMouseDown={keepEditorFocused}
    >
      <Text whiteSpace="nowrap">Visit URL:</Text>
      <ChakraLink href={href} target="_blank" rel="noopener noreferrer" truncate>
        {href}
      </ChakraLink>
      <VButton color="tertiary" onClick={onEdit}>
        Edit
      </VButton>
      <VButton color="tertiary" onClick={remove}>
        Remove
      </VButton>
    </VStackRow>
  );
};

export const RichTextEditorLinkBar = (): React.ReactElement | null => {
  const { editor, isLinkEditorOpen, openLinkEditor, closeLinkEditor } = useRichTextEditorContext();

  if (!editor) {
    return null;
  }

  if (isLinkEditorOpen) {
    return <LinkForm editor={editor} onClose={closeLinkEditor} />;
  }

  if (isCaretInLink(editor)) {
    return <LinkPreview editor={editor} onEdit={openLinkEditor} />;
  }

  return null;
};
